// utils.js composables
import { ref, onMounted, onUnmounted } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

import type { Payslip, Schedule } from '../stores/types'

// by convention, composable function names start with "use"
export function useUtil() {
  const LOG_FORMAT_STR = 'YYYY-MM-DD HH:mm:ss'

  // Reads a money value back: a number, "1234.5", or an already grouped "1,234,567.50"
  // (every comma goes, not only the first).
  function parseMoney(amount: string | number) {
    if (amount == 'NaN' || amount == undefined) {
      return 0.00
    }
    if (typeof amount == "number") {
      amount = amount.toString()
    }
    return parseFloat(amount.replace(/,/g, ''))
  }

  const moneyFormat = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  // Plain two-decimal text with no grouping ("1234567.50"). This is the form stored in the cost
  // center fields and fed back through parseMoney, so it must stay free of commas.
  function formatMoney(amount: number | undefined) {
    return amount === undefined ? amount : moneyFormat.format(amount).replace(/,/g, '')
  }

  // The same figure grouped for people to read ("1,234,567.50"). Display only: never store it or
  // bind it to an input.
  function formatMoneyGrouped(amount: number | string | undefined | null) {
    if (amount === undefined || amount === null || amount === '') return amount as undefined
    return moneyFormat.format(typeof amount === 'string' ? parseMoney(amount) : amount)
  }

  function formatDateTime(datetime: Date) {
    return dayjs(datetime).format('MM/DD/YYYY HH:mm:ss')
  }

  function formatDate(datetime: Date, formatStr = 'MM/DD/YYYY') {
    return dayjs(datetime).format(formatStr)
  }

  function formatTime(datetime: Date) {
    return dayjs(datetime).format('hh:mmA')
  }

  function utcToPhTime(utcDateStr: string) {
    // dayjs.extend(utc)
    // dayjs.extend(timezone)

    // const tz = "Asia/Manila";

    // let isUTC = dayjs(datetime).isUTC()
    // let dt = datetime

    // if (isUTC) {
    //   dt = dayjs(datetime).local().format()
    // }
    // const d = dayjs(datetime).utcOffset(8).format('hh:mmA')

    // isUTC = dayjs(dt).isUTC()

    // const utcDate = dayjs.utc(datetime);

    // Convert to the specified local timezone
    // const localDate = utcDate.tz(tz);

    const utcDate = dayjs.utc(utcDateStr)

    // Convert to local time
    const localDateStr = utcDate.local().format();


    return localDateStr
  }

  function hourDiff(startTimeStr: string, endTimeStr: string) {
    const t1 = dayjs(startTimeStr, 'YYYY-MM-DD HH:mm:ss')
    const t2 = dayjs(endTimeStr, 'YYYY-MM-DD HH:mm:ss')

    const hdiff = t2.diff(t1, 'minutes') / 60
    return hdiff
  }

  function formatDuration(datetime: Date) {
    return dayjs(datetime).format('HH:mm')
  }

  const sanitizeLogs = (logs: string) => {
    if (logs == undefined || logs.includes('undefined')) {
      return ''
    }

    const lg = logs?.trim().split(',').filter(item => item != '')


    // clean up logs diff less than 5 minutes
    const res = 30
    const max = lg.length

    let lx = [...lg].sort((a: string, b: string) => dayjs(a).valueOf() - dayjs(b).valueOf())

    for (let i = 0; i < max; i++) {
      for (let j = 0; j < max; j++) {
        if (j > i) {
          const m = dayjs(lg[j]).diff(dayjs(lg[i]), 'minutes')
          if (Math.abs(m) < res) {
            lx = lx.filter(item => item != lg[j])
          }
        }
      }

    }

    const d = dayjs(lx[0]).date()
    return dayjs(lx[0]).format('YYYY-MM-DD') + ' ' + lx.sort((a: string, b: string) => dayjs(a).valueOf() - dayjs(b).valueOf()).map(item => dayjs(item).date() == d ? dayjs(item).format('HH:mm:ss') : dayjs(item).format('YYYY-MM-DD HH:mm:ss')).join(", ")
  }

  const logHours = (logs: string, schedule: Schedule = {} as Schedule, minimumOvertime: number = 20, undertimeAllowance: number = 15, earlyIn: boolean = false, lateOut: boolean = false, lateBreak: boolean = false, earlyResume: boolean = false, roundOff: boolean=false): Array<number> => {
    if (!logs || logs.trim() === '' || logs.includes('undefined') || Object.keys(schedule).length <= 0) {
      return [0, 0, 0, 0]
    }

    // dayHours: number = 8, hoursBreak: number = 1, halfSaturday: boolean = true
    // get from Schedule
    const timeIn = schedule.time_in
    const timeOut = schedule.time_out
    const timeBreak = schedule.time_break
    const timeResume = schedule.time_resume

    // let hoursBreak = schedule.with_breaks ? dayjs(timeResume).diff(dayjs(timeBreak), 'hours') : 0
    // const am_shift =  dayjs(timeBreak).diff(dayjs(timeIn), 'hours')
    // const pm_shift = dayjs(timeOut).diff(dayjs(timeResume), 'hours')
    // const shiftHours = (am_shift > 24 ? am_shift - 24 : am_shift) + (pm_shift > 24 ? pm_shift - 24 : pm_shift) // hours to render, if more then OT

    // calc shift and break hours 
    const shift_ = sanitizeShift({
      time_in: schedule.time_in,
      time_break: schedule.time_break,
      time_resume: schedule.time_resume,
      time_out: schedule.time_out,
    }, true)


    const shift_any = shift_ as any
    const _hrs = shift_any.shift_hours[0] + shift_any.shift_hours[2]
    const shiftHours = _hrs < 0 ? _hrs + 24 : _hrs
    let hoursBreak = shift_any.shift_hours[1]
    const amHours=shift_any.shift_hours[0]
    const pmHours=shift_any.shift_hours[2]

    // HW caps at the schedule's Regular Hours; hours beyond it count as OT.
    // Schedules without day_hours fall back to the shift-span cap.
    const dayHours = Number(schedule.day_hours) > 0 ? Number(schedule.day_hours) : shiftHours



    const halfSaturday = schedule.half_saturdays

    const lg = logs.trim().split(',').filter(item => item != '').sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf())


    // clean up logs diff less than 30 minutes
    const res = 30
    const max = lg.length

    let lx = [...lg].sort()

    for (let i = 0; i < max; i++) {
      for (let j = 0; j < max; j++) {
        if (j > i) {
          const m = dayjs(lg[j]).diff(dayjs(lg[i]), 'minutes')
          if (Math.abs(m) < res) {
            lx = lx.filter(item => item != lg[j])
          }
        }
      }

    }

    // WARNING: time must be 24 format for the SORT to work
    lx = lx.map(item => dayjs(item).format('YYYY-MM-DD HH:mm:ss')).sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf())

    if (lx.length % 2 > 0) {
      return [0, 0, 0, 0]
    }

    // if no break logs or half day only
    let HW = 0
    let UT = 0
    let OT = 0

    const isSaturday = dayjs(lx[0]).day() == 6
    // Early In
    let t1 = shiftToLogStr(shift_.time_in, lx[0])
    let t2 = lx[0]
    let h_diff = hourDiff(t2, t1)
    if (earlyIn == true && h_diff > minimumOvertime / 60) {
      OT += h_diff
    }

    if (h_diff < (-undertimeAllowance / 60)) {
      UT += -h_diff
    }


    // Late Out
    // t1 = shiftToLogStr(isSaturday ? dayjs(shift_.time_out, LOG_FORMAT_STR).add(-dayHours / 2, 'hours') : shift_.time_out, lx[lx.length - 1])
    t1 = shiftToLogStr(isSaturday ? shift_.time_break : shift_.time_out, lx[lx.length - 1])
    t2 = lx[lx.length - 1]
    h_diff = hourDiff(t1, t2)
    if (lateOut == true && h_diff > (minimumOvertime / 60)) {
      OT += h_diff

      // check if there is meal break before OT
      if (lx.length>4){
        const m_break=hourDiff(lx[3],lx[4])
        if(m_break>0){
          OT-=m_break
        }
      }
    
    }
    if (h_diff <(-undertimeAllowance / 60)) {
      UT += -h_diff
    }

    // calculate diffs, logs is in PH Time
    const diffs = []
    const night_diffs = []
    for (let i = 0; i < lx.length / 2; i++) {
      const start_date = dayjs(lx[i * 2])
      const end_date = dayjs(lx[i * 2 + 1])

      let m = end_date.diff(start_date, 'minutes') / 60
      /* */
      diffs.push(m)

      // calc night diffs — overlap of [start_date, end_date] with the fixed
      // 22:00→06:00 night window. Clamped intersection is correct at every
      // boundary (including an exact 06:00 clock-out, which the previous
      // strict-inequality branches dropped to 0).
      const night_start = dayjs(lx[i * 2]).set('hour', 22).set('minute', 0).set('second', 0)
      const night_end = night_start.add(8, 'hour')
      const ov_start = start_date.isAfter(night_start) ? start_date : night_start
      const ov_end = end_date.isBefore(night_end) ? end_date : night_end
      const nightMins = ov_end.diff(ov_start, 'minutes')
      if (nightMins > 0) {
        night_diffs.push(nightMins / 60)
      }
    }
    const totalHours = diffs.reduce((tot, curr) => (tot += curr), 0)

    const totalNightHours = night_diffs.reduce((tot, curr) => (tot += curr), 0)


    const formatter = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })


    // is Saturday then halfday
    if (halfSaturday) {
      if (dayjs(lx[0]).day() == 6) {

        hoursBreak = lx.length == 2 && totalHours > amHours ? shift_any.shift_hours[1] : 0
        const netHoursSat = totalHours - hoursBreak

        HW = netHoursSat <= dayHours / 2 ? netHoursSat : dayHours / 2

        const rawOTSat = netHoursSat > dayHours / 2 ? netHoursSat - dayHours / 2 : 0
        OT = rawOTSat >= minimumOvertime / 60 ? rawOTSat : 0

      } else {
        hoursBreak = lx.length == 2 && totalHours > amHours ? shift_any.shift_hours[1] : 0
        const netHours = totalHours - hoursBreak

        HW = netHours >= dayHours ? dayHours : netHours

        UT = HW < dayHours ? dayHours - HW : 0
        const rawOT = netHours > dayHours ? netHours - dayHours : 0
        OT = rawOT >= minimumOvertime / 60 ? rawOT : 0
      }
    } else {
      hoursBreak = lx.length == 2 && totalHours > amHours ? shift_any.shift_hours[1] : 0
      const netHours = totalHours - hoursBreak

      HW = netHours >= dayHours ? dayHours : netHours
      UT = HW < dayHours ? dayHours - HW : 0
      const rawOT = netHours > dayHours ? netHours - dayHours : 0
      OT = rawOT >= minimumOvertime / 60 ? rawOT : 0
    }

    const NT = totalNightHours //night time

    return [Number(HW.toFixed(2)), Number(UT.toFixed(2)), Number(OT.toFixed(2)), Number(NT.toFixed(2))] // HW is net already of OT and UT
  }

  // Day/hour summary over a payslip's daily times; fractional days = HW / hoursInDay
  // (mirrors the payslip PDF summary in api/controllers/default.py)
  const daySummary = (dailyTimes: any[], schedules: Schedule[], hoursInDay: number = 8) => {
    const HOLIDAY_TYPES = ['RH', 'SH', 'NH']
    const LEAVE_TYPES = ['VL', 'EL', 'SL', 'ML', 'PL', 'OB']

    const summary = { workdays: 0, holidays: 0, leave: 0, absences: 0, ut: 0, ot: 0, night: 0 }

    dailyTimes
      .filter((dt: any) => dt != undefined)
      .forEach((dt: any) => {
        const schedule = schedules.find((item: Schedule) => item.uuid == dt.schedule_uuid)
        const [hw, ut, ot, nt] = logHours(dt.logs, schedule, 30, 15, dt.early_in, dt.late_out, dt.late_break, dt.early_resume)

        const workedDay = hw / hoursInDay // HW is already net of OT
        if (dt.day_type == 'WD') {
          summary.workdays += workedDay
        } else if (HOLIDAY_TYPES.includes(dt.day_type)) {
          summary.holidays += workedDay
        } else if (LEAVE_TYPES.includes(dt.day_type)) {
          summary.leave += workedDay
        } else if (dt.day_type == 'AB') {
          summary.absences += 1
        }
        summary.ut += ut
        summary.ot += ot
        summary.night += nt
      })

    return summary
  }

  // Cost-center night-differential multiplier by shift type, applied on top of
  // the base night_premium_factor (payFactor.NP, 0.10):
  //   day      → 0    (never scheduled into the 22:00–06:00 window)
  //   night    → 1    (a fixed night post — every duty hour is in the window)
  //   rotation → 1/3  (one shift in three lands at night)
  // rotation is the default: any unset/unknown shift falls back to it.
  const nightShiftFactor = (shift?: string) =>
    shift === 'night' ? 1 : shift === 'day' ? 0 : 1 / 3

  // o-datepicker calls Date methods on its v-model (focusedDate.getDate()), so a
  // string throws `TypeError: getDate is not a function` and takes the whole form
  // down — the Transact edit panel rendered blank for exactly this reason. The API
  // returns transact_date as 'YYYY-MM-DD HH:mm:ss', so it must be coerced first.
  // An unparseable or absent value yields undefined, which the datepicker accepts.
  const toDate = (value?: Date | string | null) => {
    if (value === null || value === undefined || value === '') return undefined
    // the space form is not valid ISO in every engine; 'T' makes it parse as local
    const parsed = dayjs(value instanceof Date ? value : String(value).replace(' ', 'T'))
    return parsed.isValid() ? parsed.toDate() : undefined
  }

  // The reverse: the API stores datetimes as 'YYYY-MM-DD HH:mm:ss' in PH local time.
  // JSON.stringify turns a Date into UTC ISO with a Z, which shifts PH times back 8
  // hours and can file a transaction under the previous day. Returns undefined when
  // the value cannot be parsed, so callers can leave the field untouched.
  const toApiDateTime = (value?: Date | string | null) => {
    if (value === null || value === undefined || value === '') return undefined
    const parsed = dayjs(value instanceof Date ? value : String(value).replace(' ', 'T'))
    return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : undefined
  }

  // middlename is nullable in the DB. `employee.middlename[0]` throws a TypeError
  // on a null one, and because it is called straight from templates it takes the
  // whole component down with it — the Transact edit form rendered as a blank panel
  // for exactly this reason.
  //
  // Deliberately format-neutral: it returns the bare first character, so each call
  // site keeps the punctuation it already had. `fullname` is persisted onto payslips,
  // so uppercasing or appending a '.' here would quietly change stored data. An
  // absent middlename yields '' instead of the literal 'undefined' the old string
  // concatenation produced.
  const middleInitial = (middlename?: string | null) =>
    String(middlename ?? '').trim().charAt(0)

  const sanitizeShift = (shift: any, toLocal: boolean = false) => {

    // shift is in UTC !!!
    const m = dayjs().month()
    const d = dayjs().date()
    const y = dayjs().year()

    // let time_in = dayjs.utc(shift.time_in).set('month', m).set('date', d).set('year', y)
    // let time_break = dayjs.utc(shift.time_break).set('month', m).set('date', d).set('year', y)
    // let time_resume = dayjs.utc(shift.time_resume).set('month', m).set('date', d).set('year', y)
    // let time_out = dayjs.utc(shift.time_out).set('month', m).set('date', d).set('year', y)


    // convert to PHT
    const time_in = dayjs.utc(Object.assign(shift.time_in))
    const time_break = dayjs.utc(Object.assign(shift.time_break))
    const time_resume = dayjs.utc(Object.assign(shift.time_resume))
    const time_out = dayjs.utc(Object.assign(shift.time_out))

    // let hr = [
    //   time_in.hour() + 8,
    //   time_break.hour() + 8,
    //   time_resume.hour() + 8,
    //   time_out.hour() + 8,
    // ]

    const hr = [
      time_in.hour() + time_in.minute() / 60,
      time_break.hour() + time_break.minute() / 60,
      time_resume.hour() + time_resume.minute() / 60,
      time_out.hour() + time_out.minute() / 60,
    ]

    // hr = hr.map(h => h > 24 ? h - 24 : h) //max of 24

    // const next_days = [
    //   hr[1] >= 24 ? 1 : 0,
    //   hr[2] >= 24 ? 1 : 0,
    //   hr[3] >= 24 ? 1 : 0,
    // ]

    const next_days = [
      hr[1] < hr[0] ? 1 : 0,
      hr[2] < hr[0] ? 1 : 0,
      hr[3] < hr[0] ? 1 : 0,
    ]

    let shift_hours = [
      hr[1] - hr[0],
      hr[2] - hr[1],
      hr[3] - hr[2]
    ]
    shift_hours = shift_hours.map(h => h < 0 ? h + 24 : h)

    //this is UTC because backend is UTC
    let schedule_ = {} as Schedule
    if (toLocal == true) {
      schedule_ = Object.assign({
        time_in: time_in,
        time_break: time_break,
        time_resume: time_resume,
        time_out: time_out,
        shift_hours: shift_hours,
        next_days: next_days,
        hr: hr
      })


    } else {
      schedule_ = Object.assign({
        time_in: time_in,
        time_break: time_break,
        time_resume: time_resume,
        time_out: time_out,
        shift_hours: shift_hours,
        next_days: next_days,
        hr: hr
      })

    }

    return schedule_
  }

  const shiftToLogStr = (shiftDateTime: Date, logStr: string = dayjs().format(LOG_FORMAT_STR)) => {

    const y = dayjs(logStr, 'YYYY-MM-DD HH:mm:ss').year()
    const m = dayjs(logStr, 'YYYY-MM-DD HH:mm:ss').month()
    const d = dayjs(logStr, 'YYYY-MM-DD HH:mm:ss').date()
    if (dayjs(shiftDateTime).isUTC()) {
      return dayjs.utc(shiftDateTime).add(8, 'hour').set('year', y).set('month', m).set('date', d).format('YYYY-MM-DD HH:mm:ss')
    } else {
      return dayjs(shiftDateTime).set('year', y).set('month', m).set('date', d).format('YYYY-MM-DD HH:mm:ss')
    }

  }

  /**
   * Ask before deleting. Returns true when the user confirmed.
   *
   * Delete buttons here fire on a single click with nothing in between, so a misclick
   * is destructive and silent. A native confirm is deliberate: it is modal, it cannot
   * be missed, and OK/Cancel is what was asked for. TransactList.vue uses an in-app
   * dialog instead because that action is long-running and blocking the tab would hurt;
   * a delete decision is instantaneous, so the same objection does not apply.
   *
   * One message in one place, so the wording cannot drift across the ten views.
   */
  const confirmDelete = (message = 'Press OK to confirm deletion of this record.') =>
    window.confirm(message)

  return {
    confirmDelete,
    formatMoney,
    formatMoneyGrouped,
    formatDateTime,
    formatDate,
    formatTime,
    formatDuration,
    sanitizeLogs,
    logHours,
    daySummary,
    nightShiftFactor,
    middleInitial,
    toDate,
    toApiDateTime,
    parseMoney,

    utcToPhTime,
    sanitizeShift,
    shiftToLogStr,
    hourDiff,

  }
}
